import React from 'react';
import PropTypes from 'prop-types';

const SummaryList = ({ items = [] }) => {
  if (!items || items.length === 0) {
    return <p className="text-sm text-slate-500">Vérifie et confirme pour poursuivre.</p>;
  }
  return (
    <ul className="space-y-1 text-sm text-slate-700">
      {items.map((item, index) => (
        <li key={index} className="flex items-start gap-2">
          <span className="mt-1 h-1.5 w-1.5 rounded-full bg-indigo-400" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
};

SummaryList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string),
};

const buildSummary = (tool, args) => {
  const summary = [];
  switch (tool) {
    case 'tasks__update':
      if (args.task_id) {
        summary.push(`Mettre à jour la tâche #${args.task_id}`);
      }
      if (Object.prototype.hasOwnProperty.call(args, 'is_completed')) {
        summary.push(args.is_completed ? 'La marquer comme terminée' : 'La marquer comme non terminée');
      }
      if (args.owner_name) {
        summary.push(`Assigner à ${args.owner_name}`);
      } else if (args.owner_id) {
        summary.push(`Assigner à l’employé #${args.owner_id}`);
      }
      if (args.due_date) {
        summary.push(`Nouvelle échéance au ${args.due_date}`);
      }
      if (args.priority) {
        summary.push(`Priorité : ${args.priority}`);
      }
      break;
    case 'tasks__create':
      summary.push(`Créer une nouvelle tâche "${args.name}"`);
      if (args.objective_id) {
        summary.push(`Rattachée à l’objectif #${args.objective_id}`);
      }
      if (args.project_id) {
        summary.push(`Pour le projet #${args.project_id}`);
      }
      if (args.owner_name) {
        summary.push(`Assignée à ${args.owner_name}`);
      } else if (args.owner_id) {
        summary.push(`Assignée à l’employé #${args.owner_id}`);
      }
      if (args.due_date) {
        summary.push(`Échéance fixée au ${args.due_date}`);
      }
      break;
    case 'objectives__update_status':
      if (args.objective_id) {
        summary.push(`Mettre à jour l’objectif #${args.objective_id}`);
      }
      if (args.status) {
        summary.push(`Nouveau statut : ${args.status}`);
      }
      if (args.comment) {
        summary.push('Commentaire mis à jour');
      }
      break;
    case 'objectives__create':
      summary.push(`Créer un nouvel objectif "${args.name}"`);
      if (args.project_id) {
        summary.push(`Pour le projet #${args.project_id}`);
      }
      if (args.owner_name) {
        summary.push(`Responsable : ${args.owner_name}`);
      } else if (args.owner_id) {
        summary.push(`Responsable : employé #${args.owner_id}`);
      }
      if (args.effort) {
        summary.push(`Effort estimé : ${args.effort} tokens`);
      }
      break;
    default:
      break;
  }
  return summary;
};

const ConfirmationPrompt = ({ prompt, onConfirm, onCancel, disabled = false }) => {
  const actions = prompt.actions || [];
  const hasMultipleActions = actions.length > 1;
  const summaryItems =
    prompt.summary && prompt.summary.length > 0
      ? prompt.summary
      : buildSummary(prompt.tool, prompt.arguments || {});

  const renderActionSummary = () => {
    if (!hasMultipleActions) {
      return <SummaryList items={summaryItems} />;
    }

    return (
      <div className="space-y-3">
        {actions.map((action, index) => {
          const actionSummary =
            (action.summary && action.summary.length > 0
              ? action.summary
              : buildSummary(action.tool, action.rawArguments || action.arguments || {})) || [];
          return (
            <div
              key={action.callId || action.title || `${action.tool}-${index}`}
              className="rounded-xl border border-indigo-100/70 bg-white/70 px-3 py-3"
            >
              <div className="flex items-center justify-between text-xs font-semibold uppercase text-indigo-500">
                <span>Action {index + 1}</span>
                <span className="text-indigo-400">{action.title || action.tool}</span>
              </div>
              <div className="mt-2">
                <SummaryList items={actionSummary} />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const detailPayload =
    actions.length > 0
      ? actions.map((action, index) => ({
          action: index + 1,
          tool: action.tool,
          arguments: action.rawArguments || action.arguments || {},
        }))
      : prompt.arguments || {};

  return (
    <div className="flex-1 rounded-2xl border border-indigo-200 bg-gradient-to-br from-white via-white to-indigo-50/60 p-4 shadow-md">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">Confirmation requise</p>
          <h4 className="mt-1 text-sm font-bold text-slate-900">{prompt.title || prompt.tool}</h4>
        </div>
        <span className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-medium text-indigo-600">
          {prompt.locked ? 'Action en cours…' : 'Action sécurisée'}
        </span>
      </div>

      <div className="mt-3 rounded-xl bg-white/80 px-4 py-3">
        {renderActionSummary()}

        <details className="mt-3 select-text">
          <summary className="cursor-pointer text-xs font-semibold uppercase text-indigo-400">Détails techniques</summary>
          <pre className="mt-2 max-h-48 overflow-auto rounded-lg bg-slate-900/90 px-3 py-2 text-xs text-slate-100">
            {JSON.stringify(detailPayload, null, 2)}
          </pre>
        </details>
      </div>

      <p className="mt-3 text-xs text-slate-500">{prompt.message}</p>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onConfirm}
          disabled={disabled || prompt.locked}
          className="inline-flex items-center justify-center rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Oui, confirmer
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={disabled || prompt.locked}
          className="inline-flex items-center justify-center rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Annuler
        </button>
      </div>
    </div>
  );
};

ConfirmationPrompt.propTypes = {
  prompt: PropTypes.shape({
    tool: PropTypes.string.isRequired,
    arguments: PropTypes.object,
    callId: PropTypes.string,
    batchId: PropTypes.string,
    message: PropTypes.string,
    summary: PropTypes.arrayOf(PropTypes.string),
    title: PropTypes.string,
    locked: PropTypes.bool,
    actions: PropTypes.arrayOf(
      PropTypes.shape({
        tool: PropTypes.string,
        title: PropTypes.string,
        summary: PropTypes.arrayOf(PropTypes.string),
        callId: PropTypes.string,
        rawArguments: PropTypes.object,
        arguments: PropTypes.object,
      })
    ),
  }).isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default ConfirmationPrompt;
